import React, { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { BrandingProvider } from './context/BrandingContext';
import { TenantFeaturesProvider, useTenantFeatures } from './context/TenantFeaturesContext';
import { initializeCapacitor, isNative } from './services/capacitor';
import ErrorBoundary from './components/ErrorBoundary';
import Layout from './components/Layout';
// IOSInstallPrompt intentionally not rendered — we ship a real
// native iOS/Android app via Capacitor + App Store / Play Store, so
// the 'Add to Home Screen' PWA banner just clutters mobile Safari
// for users who already have (or should install) the store app.
// Component file kept in the tree in case we want it back.
// import IOSInstallPrompt from './components/IOSInstallPrompt';
import './index.css';

// Auth pages (eagerly loaded - needed immediately)
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Verify2FA from './pages/Verify2FA';
import Features from './pages/Features';
import OnePager from './pages/OnePager';

// App pages (lazy loaded - code-split per route)
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Messages = lazy(() => import('./pages/Messages'));
const Contacts = lazy(() => import('./pages/Contacts'));
const Templates = lazy(() => import('./pages/Templates'));
const AutoResponders = lazy(() => import('./pages/AutoResponders'));
const TextBlasts = lazy(() => import('./pages/TextBlasts'));
const Reports = lazy(() => import('./pages/Reports'));
const ApiKeys = lazy(() => import('./pages/ApiKeys'));
const Integrations = lazy(() => import('./pages/Integrations'));
const Salesforce = lazy(() => import('./pages/Salesforce'));
const Teams = lazy(() => import('./pages/Teams'));
const Clio = lazy(() => import('./pages/Clio'));
const Meevo = lazy(() => import('./pages/Meevo'));
const ShipStation = lazy(() => import('./pages/ShipStation'));
const Webhooks = lazy(() => import('./pages/Webhooks'));
const Settings = lazy(() => import('./pages/Settings'));
const SystemSettings = lazy(() => import('./pages/SystemSettings'));
const AssignmentSettings = lazy(() => import('./pages/AssignmentSettings'));
const Billing = lazy(() => import('./pages/Billing'));
const Admin = lazy(() => import('./pages/Admin'));
const Logs = lazy(() => import('./pages/Logs'));
const MessageImport = lazy(() => import('./pages/MessageImport'));
const EmailToText = lazy(() => import('./pages/EmailToText'));
const Queues = lazy(() => import('./pages/Queues'));
const TenantDetail = lazy(() => import('./pages/TenantDetail'));
const Resellers = lazy(() => import('./pages/Resellers'));

// Initialize Capacitor on app load
initializeCapacitor();

const PageLoader = () => (
  <div className="flex items-center justify-center h-full min-h-[200px]">
    <div className="flex items-center gap-3 text-gray-500">
      <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
      </svg>
      Loading...
    </div>
  </div>
);

const PrivateRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-xl">Loading...</div>
      </div>
    );
  }

  return isAuthenticated ? children : <Navigate to="/login" />;
};

// Gate a route on a per-tenant feature flag. Shows the page loader while
// the flag fetch is in flight (so we don't flash content the tenant
// shouldn't see), then renders children if enabled or redirects to / if
// not. Same shape as PrivateRoute so it composes cleanly.
const FeatureGate = ({ feature, children }) => {
  const { features, loading } = useTenantFeatures();
  if (loading) return <PageLoader />;
  if (!features[feature]) return <Navigate to="/" replace />;
  return children;
};

// Every login lands on Messages — that's the primary workspace and
// where most sessions start (fresh threads, follow-ups, in-flight
// replies). The Dashboard is still reachable at /dashboard from the
// sidebar for anyone who wants the KPI view. Previously mobile went
// straight to /messages and desktop got the Dashboard; unified so
// the muscle-memory is the same on every device.
const DefaultLanding = () => <Navigate to="/messages" replace />;

const AppRoutes = () => {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/verify-2fa" element={<Verify2FA />} />
      {/* Public marketing pages — full tour + condensed one-pager. */}
      <Route path="/features" element={<Features />} />
      <Route path="/onepager" element={<OnePager />} />
      <Route
        path="/"
        element={
          <PrivateRoute>
            <Layout />
          </PrivateRoute>
        }
      >
        <Route index element={<DefaultLanding />} />
        <Route path="dashboard" element={<Suspense fallback={<PageLoader />}><Dashboard /></Suspense>} />
        <Route path="messages" element={<Suspense fallback={<PageLoader />}><Messages /></Suspense>} />
        <Route path="contacts" element={<Suspense fallback={<PageLoader />}><Contacts /></Suspense>} />
        <Route path="templates" element={<Suspense fallback={<PageLoader />}><Templates /></Suspense>} />
        <Route path="auto-responders" element={<Suspense fallback={<PageLoader />}><AutoResponders /></Suspense>} />
        <Route path="text-blasts" element={<Suspense fallback={<PageLoader />}><TextBlasts /></Suspense>} />
        <Route path="reports" element={<Suspense fallback={<PageLoader />}><Reports /></Suspense>} />
        <Route path="api-keys" element={<Suspense fallback={<PageLoader />}><ApiKeys /></Suspense>} />
        <Route path="integrations" element={<FeatureGate feature="enable_integrations"><Suspense fallback={<PageLoader />}><Integrations /></Suspense></FeatureGate>} />
        <Route path="salesforce" element={<FeatureGate feature="enable_integrations"><Suspense fallback={<PageLoader />}><Salesforce /></Suspense></FeatureGate>} />
        <Route path="teams" element={<FeatureGate feature="enable_integrations"><Suspense fallback={<PageLoader />}><Teams /></Suspense></FeatureGate>} />
        <Route path="teams/callback" element={<FeatureGate feature="enable_integrations"><Suspense fallback={<PageLoader />}><Teams /></Suspense></FeatureGate>} />
        <Route path="clio" element={<FeatureGate feature="enable_integrations"><Suspense fallback={<PageLoader />}><Clio /></Suspense></FeatureGate>} />
        <Route path="clio/callback" element={<FeatureGate feature="enable_integrations"><Suspense fallback={<PageLoader />}><Clio /></Suspense></FeatureGate>} />
        <Route path="meevo" element={<FeatureGate feature="enable_integrations"><Suspense fallback={<PageLoader />}><Meevo /></Suspense></FeatureGate>} />
        <Route path="shipstation" element={<FeatureGate feature="enable_integrations"><Suspense fallback={<PageLoader />}><ShipStation /></Suspense></FeatureGate>} />
        <Route path="webhooks" element={<FeatureGate feature="enable_integrations"><Suspense fallback={<PageLoader />}><Webhooks /></Suspense></FeatureGate>} />
        <Route path="admin" element={<Suspense fallback={<PageLoader />}><Admin /></Suspense>} />
        <Route path="admin/tenants/:id" element={<Suspense fallback={<PageLoader />}><TenantDetail /></Suspense>} />
        <Route path="billing" element={<Suspense fallback={<PageLoader />}><Billing /></Suspense>} />
        <Route path="logs" element={<Suspense fallback={<PageLoader />}><Logs /></Suspense>} />
        <Route path="settings" element={<Suspense fallback={<PageLoader />}><Settings /></Suspense>} />
        <Route path="system-settings" element={<Suspense fallback={<PageLoader />}><SystemSettings /></Suspense>} />
        <Route path="assignment-settings" element={<Suspense fallback={<PageLoader />}><AssignmentSettings /></Suspense>} />
        <Route path="message-import" element={<Suspense fallback={<PageLoader />}><MessageImport /></Suspense>} />
        <Route path="email-to-text" element={<Suspense fallback={<PageLoader />}><EmailToText /></Suspense>} />
        <Route path="queues" element={<Suspense fallback={<PageLoader />}><Queues /></Suspense>} />
        <Route path="resellers" element={<Suspense fallback={<PageLoader />}><Resellers /></Suspense>} />
        <Route path="resellers/:id" element={<Suspense fallback={<PageLoader />}><Resellers /></Suspense>} />
      </Route>
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  );
};

function App() {
  return (
    <ErrorBoundary>
      <BrandingProvider>
        <AuthProvider>
          <TenantFeaturesProvider>
            <Router>
              <AppRoutes />
            </Router>
          </TenantFeaturesProvider>
        </AuthProvider>
      </BrandingProvider>
    </ErrorBoundary>
  );
}

export default App;
